// Верхняя панель приложения: путешественники, валюта, справочник курсов.
// Сворачивание боковых панелей живёт не здесь, а на рейках по краям (см. App.tsx).
//
// Под мобильным порогом (useIsMobile) полосу целиком заменяет MobileShellHeader
// — общая для планировщика и /trips мобильная шапка с лого-полосой, заголовком
// и единым сайдбаром вместо бургера с попапом аккаунта (см. MobileShellHeader.tsx).
// Toolbar по-прежнему собирает сами контролы (травелеры, валюта, курсы) и на
// узком экране передаёт их туда разделом «Поездка» — состояние и разметка тех
// же контролов общие для обеих раскладок, только упаковка разная.
import { useState } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useActions, useStore } from '../../store';
import { BASE_CURRENCY, type Currency } from '../../types';
import { useT } from '../../i18n';
import { useAuth } from '../../auth';
import { CurrenciesModal } from './CurrenciesModal';
import { Logo } from './Logo';
import { AppMenuBlock, AppMenuRow } from './AppMenu';
import { MobileShellHeader } from './MobileShellHeader';
import { useIsMobile } from './useIsMobile';
import styles from './Toolbar.module.css';

/** Иконки полей: подписи вынесены в подсказки, чтобы полоса влезала в одну строку. */

/**
 * Единственная иконка полосы крупнее остальных: у них под собой есть кнопка с
 * рамкой, а эта стоит голой и на общем размере терялась рядом со степпером.
 * 29px — потолок при строке --ctl-h в 35px: пара пикселей сверху и снизу нужна,
 * чтобы иконка не упиралась в край.
 */
function TravelersIcon() {
  return (
    <svg
      width="29"
      height="29"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
      <path d="M16 5.4a3.2 3.2 0 0 1 0 5.2" />
      <path d="M17.5 14.6a5.5 5.5 0 0 1 3 4.9" />
    </svg>
  );
}

function RatesIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 8h13l-2.5-2.5M20 16H7l2.5 2.5" />
    </svg>
  );
}

export interface ToolbarProps {
  /** Слот справа: сюда App кладёт снимки и историю. */
  actions?: ReactNode;
  /**
   * Название открытой поездки. Слот, а не собственный контрол полосы: список
   * поездок живёт в src/trips, и оболочка про него ничего не знает.
   */
  tripName?: ReactNode;
  /**
   * Начало группы контролов поездки, слева от путешественников. Сюда попадает
   * то, с чего работу с поездкой начинают, а не то, чем её настраивают.
   * В бургер-меню уезжает первым блоком того же раздела.
   */
  lead?: ReactNode;
  /**
   * Открыть форму входа сразу — например, по ссылке из письма о сбросе
   * пароля. Пробрасывается только в мобильный сайдбар: тот сам собирает
   * свою кнопку аккаунта (см. MobileShellHeader), а десктопная кнопка в
   * `actions` получает этот же флаг напрямую от App.tsx, минуя Toolbar.
   */
  accountInitialSignIn?: boolean;
}

export function Toolbar({
  actions: actionsSlot,
  accountInitialSignIn,
  tripName,
  lead,
}: ToolbarProps = {}): ReactElement {
  const state = useStore();
  const actions = useActions();
  const t = useT();
  const auth = useAuth();

  const [currenciesOpen, setCurrenciesOpen] = useState(false);
  const isMobile = useIsMobile();

  // Гостю и во время загрузки статуса знак ведёт на корень: список поездок —
  // это экран вошедшего пользователя, показывать его раньше рано.
  const logoHref = auth.status === 'user' ? '/trips' : '/';

  function handleDecTravelers(): void {
    actions.setTravelers(state.travelers - 1);
  }

  function handleIncTravelers(): void {
    actions.setTravelers(state.travelers + 1);
  }

  function handleCurrencyChange(e: React.ChangeEvent<HTMLSelectElement>): void {
    actions.setDisplayCurrency(e.target.value as Currency);
  }

  // Контролы собраны один раз: мобильная полоса и десктопная раскладывают их
  // по-разному, но разметка и состояние у них общие.
  const travelersStepper = (
    <div className={styles.stepper}>
      <button
        type="button"
        className={`btn btn-sm ${styles.ctl} ${styles.ctlIcon}`}
        onClick={handleDecTravelers}
        disabled={state.travelers <= 1}
        aria-label={t('toolbar.travelers.dec')}
      >
        −
      </button>
      <span className={`mono ${styles.stepperValue}`} aria-live="polite">
        {state.travelers}
      </span>
      <button
        type="button"
        className={`btn btn-sm ${styles.ctl} ${styles.ctlIcon}`}
        onClick={handleIncTravelers}
        aria-label={t('toolbar.travelers.inc')}
      >
        +
      </button>
    </div>
  );

  const ratesButton = (
    <button
      type="button"
      className={`btn btn-sm ${styles.ctl} ${styles.ctlIcon}`}
      onClick={() => setCurrenciesOpen(true)}
      aria-label={t('toolbar.rates')}
      title={t('toolbar.rates.tip', { base: BASE_CURRENCY })}
    >
      <RatesIcon />
    </button>
  );

  const currencySelect = (
    <select
      id="currency-select"
      className={`select ${styles.ctl}`}
      value={state.displayCurrency}
      onChange={handleCurrencyChange}
      aria-label={t('toolbar.currency')}
      title={t('toolbar.currency.tip')}
    >
      {state.currencies.map((c) => (
        <option key={c.code} value={c.code}>
          {c.symbol ? `${c.code} ${c.symbol}` : c.code}
        </option>
      ))}
    </select>
  );

  const ratesModal = currenciesOpen && <CurrenciesModal onClose={() => setCurrenciesOpen(false)} />;

  // На узком экране название поездки становится заголовком строки, а не
  // строкой внутри сайдбара (см. MobileShellHeader) — там для него отдельного
  // места больше нет, вместо него в раздел «Поездка» едет всё остальное.
  //
  // tripName пробрасывается в title как есть, вплоть до undefined: у гостя в
  // планировщике открытой поездки нет никогда, и вызывающий экран (App.tsx,
  // см. комментарий у пропа выше) передаёт честное отсутствие узла вместо
  // <TripNameField/>, который в этом случае всё равно отрисовался бы в null.
  // Что делать с пустым заголовком — забота MobileShellHeader (однострочная
  // раскладка вместо пустого <h1>, см. компонент), а не Toolbar: он про
  // список поездок ничего не знает и подменить пропавший узел не может.
  if (isMobile) {
    return (
      <>
        <MobileShellHeader
          title={tripName}
          accountInitialSignIn={accountInitialSignIn}
          tripSection={
            <>
              {lead && <AppMenuBlock>{lead}</AppMenuBlock>}
              <AppMenuRow label={t('toolbar.travelers')} className={styles.menuRow}>
                {travelersStepper}
              </AppMenuRow>
              <AppMenuRow label={t('toolbar.currency')} className={styles.menuRow}>
                {currencySelect}
              </AppMenuRow>
              <AppMenuRow label={t('toolbar.rates')} className={styles.menuRow}>
                {ratesButton}
              </AppMenuRow>
              {/* Снимки и история — про поездку, а не про аккаунт: они
                  хранят её состояние, у каждой поездки своё. В разделе
                  аккаунта две безымянные иконки висели бы в конце списка,
                  и было бы не понять, к чему они относятся. */}
              {actionsSlot && (
                <AppMenuRow label={t('toolbar.snapshots')} className={styles.menuRow}>
                  {actionsSlot}
                </AppMenuRow>
              )}
            </>
          }
        />
        {ratesModal}
      </>
    );
  }

  return (
    <header className={styles.toolbar}>
      <div className={styles.brand}>
        {/* Знак ведёт в список поездок для вошедшего и на корень для гостя —
            до входа список поездок ему всё равно недоступен. */}
        <Logo title={t('app.brand')} className={styles.brandLogo} href={logoHref} />
        {/* Название открытой поездки — сразу за знаком: знак говорит, что это
            за приложение, название — над чем человек сейчас работает. */}
        {tripName}
      </div>

      <div className={styles.settings}>
        {lead}

        <div
          className={styles.field}
          role="group"
          aria-label={t('toolbar.travelers')}
          title={t('toolbar.travelers')}
        >
          <span className={styles.fieldIcon}>
            <TravelersIcon />
          </span>
          {travelersStepper}
        </div>

        {/* Справочник курсов стоит слева от выбора валюты: обе кнопки про одно
            и то же, а иконка — вход в редактирование того, что показывает select. */}
        <div className={styles.field}>
          {ratesButton}
          {currencySelect}
        </div>
      </div>

      {/* Персонализация — самая правая иконка полосы: она про самого человека,
          а не про поездку, поэтому стоит после всех рабочих кнопок. */}
      <div className={styles.actions}>{actionsSlot}</div>

      {ratesModal}
    </header>
  );
}
